import React, { useMemo, useState } from "react";
import {
  Button,
  Alert,
  Modal,
  ModalDialog,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/joy";
import ConfirmationModal from "../../../../components/Modals/ConfirmationModal";
import { DocumentTypeResponse } from "../../types/documentTypes";

interface DocumentTypeVersionRollbackProps {
  documentType: DocumentTypeResponse;
  versions: DocumentTypeResponse[] | null;
  loadError?: string | null;
  loading?: boolean;
  onRollback: (targetVersion: number) => Promise<void>;
}

const DocumentTypeVersionRollback: React.FC<
  DocumentTypeVersionRollbackProps
> = ({ documentType, versions, loadError, loading = false, onRollback }) => {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] =
    useState<DocumentTypeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const priorVersions = useMemo(
    () =>
      (versions ?? []).filter(
        (version) => version.Version < documentType.Version,
      ),
    [documentType.Version, versions],
  );
  const visible = priorVersions.length > 0;
  const versionsToDiscard = selectedVersion
    ? (versions ?? [])
        .filter(
          (version) =>
            version.Version > selectedVersion.Version &&
            version.Version <= documentType.Version,
        )
        .sort((left, right) => left.Version - right.Version)
    : [];

  const confirmRollback = async () => {
    if (!selectedVersion) return;
    setError(null);
    try {
      await onRollback(selectedVersion.Version);
      setSelectedVersion(null);
    } catch (err: any) {
      setError(err.message || "Could not roll back this document type.");
    }
  };

  if (!visible) {
    return loadError ? (
      <Alert color="danger" role="alert">
        {loadError}
      </Alert>
    ) : null;
  }

  return (
    <>
      {loadError && (
        <Alert color="danger" role="alert">
          {loadError}
        </Alert>
      )}
      <Button
        variant="outlined"
        size="sm"
        onClick={() => setSelectorOpen(true)}
        aria-label={`Version ${documentType.Version}, ${documentType.Status}. Select an earlier version`}
      >
        Version {documentType.Version} · {documentType.Status}
      </Button>
      <Modal open={selectorOpen} onClose={() => setSelectorOpen(false)}>
        <ModalDialog>
          <DialogTitle>Select an earlier version</DialogTitle>
          <Stack spacing={1}>
            {priorVersions
              .slice()
              .sort((left, right) => right.Version - left.Version)
              .map((version) => (
                <Button
                  key={version.DocumentTypeID}
                  variant="outlined"
                  color="neutral"
                  onClick={() => {
                    setSelectedVersion(version);
                    setSelectorOpen(false);
                    setError(null);
                  }}
                >
                  <Stack alignItems="flex-start">
                    <Typography level="title-sm">
                      Version {version.Version} · {version.Status}
                    </Typography>
                    <Typography level="body-xs">
                      {new Date(version.CreatedDate).toLocaleDateString()}
                    </Typography>
                  </Stack>
                </Button>
              ))}
          </Stack>
        </ModalDialog>
      </Modal>
      <ConfirmationModal
        open={selectedVersion !== null}
        title="Confirm irreversible rollback"
        message={
          <Stack spacing={1}>
            <Typography>
              This action is IRREVERSIBLE. It will publish version{" "}
              {selectedVersion?.Version}
              and discard these newer versions:
            </Typography>
            <ul>
              {versionsToDiscard.map((version) => (
                <li key={version.DocumentTypeID}>
                  Version {version.Version} · {version.Status}
                </li>
              ))}
            </ul>
          </Stack>
        }
        actionLabel={loading ? "Rolling back..." : "Roll back"}
        onAction={confirmRollback}
        onClose={() => {
          if (!loading) {
            setSelectedVersion(null);
            setError(null);
          }
        }}
        loading={loading}
        error={error}
      />
    </>
  );
};

export default DocumentTypeVersionRollback;
